import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileImage, FileText } from "lucide-react";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { fileUrl } from "../lib/api";

const HORSE_PLACEHOLDER = "https://images.pexels.com/photos/32076745/pexels-photo-32076745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Limit the visualization to 5 generations (root + 4 ancestor generations).
const MAX_DEPTH = 5;       // levels including root (gen 0..4)
const MAX_GEN = MAX_DEPTH - 1;       // last generation index (4)
const TOTAL_ROWS = 1 << MAX_GEN;     // 16 base rows

// Sizing presets: shrink everything on mobile so the tree doesn't become a giant tall canvas.
const DESKTOP = { col: 200, row: 96, photo: 56, font: 12, smallFont: 10 };
const MOBILE  = { col: 132, row: 60, photo: 36, font: 10, smallFont: 9 };

const useIsMobile = () => {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onR = () => setM(window.innerWidth < 640);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return m;
};

const flattenByGeneration = (root) => {
  const levels = [[{ node: root, pos: 1 }]];
  for (let g = 0; g < MAX_DEPTH - 1; g++) {
    const current = levels[g];
    const next = [];
    for (const item of current) {
      const n = item.node;
      next.push({ node: n?.father || null, pos: item.pos * 2 });
      next.push({ node: n?.mother || null, pos: item.pos * 2 + 1 });
    }
    levels.push(next);
  }
  return levels;
};

const analyzeDuplicates = (levels) => {
  const byId = new Map();
  levels.forEach((gen, gIdx) => {
    gen.forEach((item) => {
      if (!item.node) return;
      const bits = item.pos.toString(2).slice(1);
      const sideFromPath = bits.length > 0 ? (bits[0] === "0" ? "P" : "M") : "self";
      const entry = byId.get(item.node.id) || { count: 0, hits: [] };
      entry.count += 1;
      entry.hits.push({ gen: gIdx, side: sideFromPath });
      byId.set(item.node.id, entry);
    });
  });
  const colorMap = new Map();
  const labelMap = new Map();
  let colorIdx = 0;
  byId.forEach((entry, id) => {
    const nonSelf = entry.hits.filter((h) => h.gen > 0);
    if (nonSelf.length < 2) return;
    colorMap.set(id, colorIdx % 6);
    colorIdx += 1;
    const paternal = nonSelf.filter((h) => h.side === "P").map((h) => h.gen).sort((a, b) => a - b);
    const maternal = nonSelf.filter((h) => h.side === "M").map((h) => h.gen).sort((a, b) => a - b);
    const parts = [];
    if (paternal.length) parts.push(paternal.join(","));
    if (maternal.length) parts.push(maternal.join(","));
    labelMap.set(id, parts.join("x"));
  });
  return { colorMap, labelMap };
};

const isFemale = (g) => g === "Гүү" || g === "Үрээ";

const Cell = ({ node, colorIdx, dupLabel, sizes }) => {
  const photoSize = sizes.photo;
  if (!node) {
    return (
      <div
        className="w-full h-full border border-dashed border-[#D6CFC4] bg-[#F0EBE1]/10 flex flex-col items-center justify-center text-center px-1 py-1"
        data-testid="pedigree-node-empty"
      >
        <span className="italic text-[#9C8C7A]" style={{ fontSize: `${sizes.smallFont}px` }}>Мэдээлэлгүй</span>
      </div>
    );
  }
  const fem = isFemale(node.gender);
  const photo = node.photo_path ? fileUrl(node.photo_path) : null;
  const ringCls = colorIdx !== null && colorIdx !== undefined ? `dup-ring-${colorIdx}` : "";
  return (
    <Link
      to={`/horse/${node.id}`}
      className={`relative w-full h-full border border-[#D6CFC4] bg-white hover:bg-[#F0EBE1]/40 transition-colors flex flex-col items-center justify-center text-center px-2 py-1 gap-0.5 ${ringCls}`}
      data-testid={`pedigree-node-${node.id}`}
    >
      {photo ? (
        <img
          src={photo}
          alt={node.name}
          className="object-cover rounded-full border-2 border-[#D6CFC4] shadow-sm"
          style={{ width: `${photoSize}px`, height: `${photoSize}px` }}
        />
      ) : (
        <div
          className="rounded-full border-2 border-dashed border-[#D6CFC4] bg-[#F0EBE1]/40 flex items-center justify-center text-[#9C8C7A]"
          style={{ width: `${photoSize}px`, height: `${photoSize}px`, fontSize: `${sizes.smallFont}px` }}
        >
          {fem ? "♀" : "♂"}
        </div>
      )}
      <div
        className={`font-semibold leading-tight ${fem ? "text-pink-600" : "text-blue-600"}`}
        style={{ fontSize: `${sizes.font}px` }}
      >
        {node.name}{" "}
        <span className="font-bold">{fem ? "♀" : "♂"}</span>
      </div>
      {node.birth_year && (
        <div className="text-[#5C4F42]" style={{ fontSize: `${sizes.smallFont}px` }}>{node.birth_year}</div>
      )}
      {dupLabel && (
        <span className={`absolute top-0.5 right-0.5 font-bold px-1 py-0.5 rounded dup-${colorIdx}`} style={{ fontSize: `${sizes.smallFont}px` }}>
          {dupLabel}
        </span>
      )}
    </Link>
  );
};

const PedigreeTree = ({ tree, horse }) => {
  const treeRef = useRef(null);
  const captureRef = useRef(null);
  const headerRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'png' | 'pdf' | null
  const isMobile = useIsMobile();
  const sizes = isMobile ? MOBILE : DESKTOP;
  const COL_MIN_WIDTH = sizes.col;
  const ROW_MIN_HEIGHT = sizes.row;

  if (!tree) return null;
  const levels = flattenByGeneration(tree);
  const { colorMap, labelMap } = analyzeDuplicates(levels);

  const duplicateLegend = [];
  colorMap.forEach((cIdx, id) => {
    const label = labelMap.get(id);
    for (const lvl of levels) {
      const found = lvl.find((x) => x.node && x.node.id === id);
      if (found) {
        duplicateLegend.push({ id, name: found.node.name, colorIdx: cIdx, label });
        break;
      }
    }
  });

  // Build a flat list of cells with grid placement (col, rowStart, rowSpan)
  const cells = [];
  levels.forEach((gen, gIdx) => {
    const span = 1 << (MAX_GEN - gIdx);
    gen.forEach((item, idx) => {
      cells.push({
        key: `g${gIdx}-i${idx}`,
        col: gIdx + 1,
        rowStart: idx * span + 1,
        rowSpan: span,
        node: item.node,
        colorIdx: item.node ? colorMap.get(item.node.id) : null,
        dupLabel: item.node ? labelMap.get(item.node.id) : null,
      });
    });
  });

  const safeFileBase = () => {
    const name = (tree?.name || "udmiin-mod").toString().trim().replace(/\s+/g, "_");
    return `${name}_udmiin_mod`;
  };

  const formatToday = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
  };

  const captureTreePng = async () => {
    const target = captureRef.current;
    const header = headerRef.current;
    const treeBox = treeRef.current;
    if (!target || !treeBox) return null;
    // Show header for capture
    if (header) header.style.display = "block";
    try {
      const fullWidth = Math.max(treeBox.scrollWidth, target.scrollWidth);
      const fullHeight = target.scrollHeight;
      return await toPng(target, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#FFFFFF",
        style: { overflow: "visible" },
        width: fullWidth,
        height: fullHeight,
      });
    } finally {
      if (header) header.style.display = "none";
    }
  };

  const downloadPng = async () => {
    try {
      setBusy("png");
      const dataUrl = await captureTreePng();
      if (!dataUrl) return;
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${safeFileBase()}.png`;
      a.click();
    } catch (e) {
      console.error("PNG download failed", e);
      alert("PNG татаж авахад алдаа гарлаа.");
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async () => {
    try {
      setBusy("pdf");
      const dataUrl = await captureTreePng();
      if (!dataUrl) return;
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => { img.onload = res; });
      const w = img.width;
      const h = img.height;
      const orientation = w >= h ? "l" : "p";
      const pdf = new jsPDF({ orientation, unit: "px", format: [w, h] });
      pdf.addImage(dataUrl, "PNG", 0, 0, w, h);
      pdf.save(`${safeFileBase()}.pdf`);
    } catch (e) {
      console.error("PDF download failed", e);
      alert("PDF татаж авахад алдаа гарлаа.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
        <button
          onClick={downloadPng}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#1B3B5A] text-[#1B3B5A] rounded hover:bg-[#1B3B5A] hover:text-white transition-colors disabled:opacity-50"
          data-testid="download-pedigree-png"
        >
          <FileImage className="w-3.5 h-3.5" />
          {busy === "png" ? "Татаж байна..." : "PNG татах"}
        </button>
        <button
          onClick={downloadPdf}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#8B1A1A] text-[#8B1A1A] rounded hover:bg-[#8B1A1A] hover:text-white transition-colors disabled:opacity-50"
          data-testid="download-pedigree-pdf"
        >
          <FileText className="w-3.5 h-3.5" />
          {busy === "pdf" ? "Татаж байна..." : "PDF татах"}
        </button>
      </div>

      {duplicateLegend.length === 0 ? (
        <div className="mb-4 text-sm text-[#2C241B] border border-[#D6CFC4] bg-[#F0EBE1]/40 rounded p-3" data-testid="no-duplicates">
          5 үе дотор төрлийн нийлүүлэг байхгүй
        </div>
      ) : (
        <div className="mb-4 text-sm border border-[#D6CFC4] bg-[#F0EBE1]/40 rounded p-3">
          <div className="font-bold text-[#1B3B5A] mb-2">төрлийн нийлүүлэг:</div>
          <div className="flex flex-wrap gap-2">
            {duplicateLegend.map((d) => (
              <span key={d.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded dup-${d.colorIdx} font-bold`} data-testid={`dup-legend-${d.id}`}>
                <Link to={`/horse/${d.id}`} className="hover:underline">{d.name}</Link>
                <span className="text-[10px] opacity-80">({d.label})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div ref={captureRef} className="w-full bg-white" data-testid="pedigree-capture">
        <div
          ref={headerRef}
          style={{
            display: "none",
            padding: "20px 24px",
            borderBottom: "2px solid #1B3B5A",
            backgroundColor: "#FFFFFF",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "24px" }}>
            <div>
              <div style={{ fontSize: "11px", letterSpacing: "2px", color: "#B8860B", fontWeight: 700, textTransform: "uppercase" }}>
                Дарьганга зээрднүүд — Угшлын гэрчилгээ
              </div>
              <div style={{ fontSize: "24px", fontWeight: 800, color: "#1B3B5A", marginTop: "4px" }}>
                {horse?.name || tree?.name || "—"}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", marginTop: "10px", fontSize: "12px", color: "#2C241B" }}>
                {(horse?.registration_number || tree?.registration_number) && (
                  <div><b>Бүртгэлийн дугаар:</b> {horse?.registration_number || tree?.registration_number}</div>
                )}
                {horse?.microchip && <div><b>Микрочип:</b> {horse.microchip}</div>}
                {(horse?.birth_year || tree?.birth_year) && (
                  <div><b>Төрсөн он:</b> {horse?.birth_year || tree?.birth_year}</div>
                )}
                {(horse?.color || tree?.color) && (
                  <div><b>Зүс:</b> {horse?.color || tree?.color}</div>
                )}
                {(horse?.gender || tree?.gender) && (
                  <div><b>Хүйс:</b> {horse?.gender || tree?.gender}</div>
                )}
                {horse?.owner_name && <div><b>Эзэмшигч:</b> {horse.owner_name}</div>}
                {horse?.breeder_farm && <div><b>Үржүүлэгч:</b> {horse.breeder_farm}</div>}
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: "11px", color: "#5C4F42", whiteSpace: "nowrap" }}>
              <div><b>Огноо:</b> {formatToday()}</div>
              <div style={{ marginTop: "4px" }}>5 үеийн удмын мод</div>
            </div>
          </div>
        </div>

        <div className="w-full overflow-x-auto scroll-soft" data-testid="pedigree-tree">
          <div
            ref={treeRef}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${MAX_DEPTH}, minmax(${COL_MIN_WIDTH}px, 1fr))`,
              gridTemplateRows: `repeat(${TOTAL_ROWS}, minmax(${ROW_MIN_HEIGHT}px, auto))`,
              minWidth: COL_MIN_WIDTH * MAX_DEPTH,
              width: "100%",
              backgroundColor: "#FFFFFF",
            }}
          >
            {cells.map((c) => (
              <div
                key={c.key}
                style={{
                  gridColumn: c.col,
                  gridRow: `${c.rowStart} / span ${c.rowSpan}`,
                }}
              >
                <Cell node={c.node} colorIdx={c.colorIdx} dupLabel={c.dupLabel} sizes={sizes} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PedigreeTree;
